import { useCallback, useEffect, useMemo, useRef } from "react";
import _ from "lodash";

import useFirestore from "hooks/useFirestore";
import { DEFAULT_DELAY_SETTINGS } from "utils/contacts/bulkMessage/new";
import type { BulkMessageDelaySettings } from "utils/contacts/bulkMessage/new";

/** The key `NewBulkMessageDialog` used with `useLocalStorage` before this hook existed. */
const LEGACY_LOCAL_STORAGE_KEY = "bulk-message-delay-settings";

/**
 * The realtor's saved bulk-send pacing, read from `userSettings.bulkMessageDelaySettings`.
 *
 * Before this hook the controls were `useLocalStorage` inside `NewBulkMessageDialog`. That
 * persisted, but only in the browser that set it: the same realtor sending from a laptop
 * and from their phone had two different pacings, a cleared profile silently reset to the
 * fast default, and there was no surface anywhere that showed what the current pacing
 * even was. It is an account preference, so it lives on the account.
 *
 * ── Absent means DEFAULTS, per field ─────────────────────────────────────────────────
 *
 * The stored value is merged over the defaults rather than replacing them, so a field
 * added to `BulkMessageDelaySettings` later is populated for the users who already have
 * a saved object. Without the merge those users would read `undefined` for the new field
 * and the send loop would compute delays from `NaN`.
 */
export function useBulkMessageDelaySettings(): {
  delaySettings: BulkMessageDelaySettings;
  /** True while nothing has been saved yet, so the UI can say "defaults" rather than lie. */
  isDefault: boolean;
  saveDelaySettings: (next: BulkMessageDelaySettings) => Promise<void>;
  resetDelaySettings: () => Promise<void>;
} {
  const { userSettings, updateUserSettings } = useFirestore();

  const stored = userSettings?.bulkMessageDelaySettings;

  const delaySettings = useMemo<BulkMessageDelaySettings>(
    () => ({ ...DEFAULT_DELAY_SETTINGS, ...(stored ?? {}) }),
    [stored]
  );

  const saveDelaySettings = useCallback(
    async (next: BulkMessageDelaySettings): Promise<void> => {
      // Clamped here rather than only in the form, because this is the chokepoint every
      // writer goes through — the settings section AND the send dialog's own panel. A max
      // below the min makes the extension's `random(min, max)` return NaN and stall the
      // whole send.
      const delayMinSeconds = Math.max(0, Math.round(next.delayMinSeconds));

      await updateUserSettings({
        bulkMessageDelaySettings: {
          delayMinSeconds,
          delayMaxSeconds: Math.max(delayMinSeconds, Math.round(next.delayMaxSeconds)),
          cooldownEnabled: next.cooldownEnabled,
          cooldownAfterCount: Math.max(1, Math.round(next.cooldownAfterCount)),
          cooldownDurationSeconds: Math.max(60, Math.round(next.cooldownDurationSeconds))
        }
      });
    },
    [updateUserSettings]
  );

  const resetDelaySettings = useCallback(
    () => saveDelaySettings(DEFAULT_DELAY_SETTINGS),
    [saveDelaySettings]
  );

  /**
   * ONE-TIME MIGRATION off the old `localStorage` key.
   *
   * Realtors who deliberately slowed their sending down are exactly the ones who must not
   * silently get the fast default back on the deploy that moves this to Firestore — so a
   * browser holding a non-default legacy value promotes it to the account before anything
   * reads the field.
   *
   * Guarded three ways: only when nothing is stored on the account (`stored` absent, so a
   * real choice can never be overwritten), only when the legacy value actually DIFFERS
   * from the defaults (otherwise every user who ever opened the dialog would get a
   * settings write for nothing), and once per mount via the ref (`userSettings` changes
   * identity on every snapshot).
   *
   * The key is left in place rather than removed: a rollback would need it, and a stale
   * key is read by nothing after this.
   */
  const hasAttemptedMigration = useRef(false);
  useEffect(() => {
    if (hasAttemptedMigration.current) return;
    // `userSettings` undefined means the document has not arrived yet — migrating now
    // would read `stored` as absent for a user who has one.
    if (!userSettings) return;
    hasAttemptedMigration.current = true;
    if (stored) return;

    let legacy: Partial<BulkMessageDelaySettings> | null = null;
    try {
      const raw = localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY);
      legacy = raw ? JSON.parse(raw) : null;
    } catch {
      // Corrupt JSON, or storage blocked in a privacy mode. Defaults are correct here.
      return;
    }
    if (!legacy) return;

    const merged = { ...DEFAULT_DELAY_SETTINGS, ...legacy };
    if (_.isEqual(merged, DEFAULT_DELAY_SETTINGS)) return;

    saveDelaySettings(merged).catch((error) => console.error(error));
  }, [userSettings, stored, saveDelaySettings]);

  return { delaySettings, isDefault: !stored, saveDelaySettings, resetDelaySettings };
}
